import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { RecordNav } from "../components/RecordNav";
import { PostingDateHint } from "../components/PostingDateHint";
import { vendorItemCost } from "../components/VendorPrices";
import { ReceiveDialog } from "../components/ReceiveDialog";
import { qtyReceived, qtyToInvoice, poCanReceive, poCanInvoice, poHasReceipts } from "../lib/receiving";

const docKey = (d) => d.doc_no;
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, FloppyDisk, CaretDown, CaretRight, Plus, Trash, ArrowsClockwise,
  SealCheck, Sparkle, Note as NoteIcon, ListChecks, CurrencyDollar, FilePdf, ArrowUUpLeft, EnvelopeSimple, PaperPlaneTilt, Hourglass, CheckCircle, XCircle, Circle, Paperclip,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { money, moneyIn, formatApiErrorDetail, API, localToday } from "../lib/api";
import api from "../lib/api";
import { useAutoSave, AutoSaveIndicator } from "../lib/useAutoSave";
import { StatusChip, ConfirmDialog } from "../components/shared";
import { AddressFields, IncotermsFields } from "../components/AddressFields";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DocNoField } from "../components/DocNoField";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Field, ReadOnly, RibbonBtn, PaneRow } from "../components/DocumentCardParts";
import { itemLineDescription, LinePartNo, isMobileViewport, UseItemDescription, ItemPicker } from "../components/LineItemText";
import { AttachmentsPane } from "../components/AttachmentsPane";
import { usePerms } from "../lib/perms";
import { ViewOnlyBadge } from "../components/Permission";
import { EmailAttachmentPicker } from "../components/EmailAttachmentPicker";
import { POEmailMessage } from "../components/POEmailMessage";
import { CURRENCIES } from "../lib/constants";

const PDOC_LABEL = { Quote: "Purchase Quote", Order: "Purchase Order", Invoice: "Purchase Invoice", CreditMemo: "Purchase CR/Adj Note" };
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function Section({ title, subtitle, defaultOpen = true, mobileCollapsed = false, children }) {
  const [open, setOpen] = useState(defaultOpen && !(mobileCollapsed && isMobileViewport()));
  const key = title.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <div className="border border-border bg-card mb-4" data-testid={`psection-${key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-secondary/50 transition-colors" data-testid={`psection-toggle-${key}`}>
        {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
        <span className="font-bold text-sm tracking-tight">{title}</span>
        {subtitle && <span className="text-xs text-muted-foreground ml-1">— {subtitle}</span>}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

export default function PurchaseOrderCard() {
  const { id } = useParams();
  const isNew = id === "new";
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const backTab = sp.get("tab") || "orders";
  const goBack = () => navigate(`/purchasing?tab=${backTab}`);

  const { data: doc, isLoading } = useQuery({
    queryKey: ["purchase-order", id],
    queryFn: async () => (await api.get(`/purchase-orders/${id}`)).data,
    enabled: !isNew,
  });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data });
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });
  const { data: salespeople = [] } = useQuery({ queryKey: ["salespeople"], queryFn: async () => (await api.get("/salespeople")).data });
  const { data: allSalesOrders = [] } = useQuery({ queryKey: ["sales-orders"], queryFn: async () => (await api.get("/sales-orders")).data });
  const { data: approvalConfig } = useQuery({ queryKey: ["po-approval-config"], queryFn: async () => (await api.get("/po-approval-config")).data, retry: false });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: fxData } = useQuery({ queryKey: ["fx-rates"], queryFn: async () => (await api.get("/fx/rates")).data, staleTime: 60 * 60 * 1000 });
  const salesOrderOptions = allSalesOrders.filter((o) => o.doc_type === "Order" && !o.archived);

  const docType = isNew ? (sp.get("type") || "Order") : (doc?.doc_type || "Order");
  const { can, isAdmin } = usePerms();
  const viewOnly = !can("purchasing.edit");
  const canPost = can("purchasing.post");
  const readOnly = viewOnly || !!doc?.posted || !!doc?.archived || ["Received", "Converted"].includes(doc?.status) || poHasReceipts(doc);
  const [receiveOpen, setReceiveOpen] = useState(false);

  const [form, setForm] = useState({
    vendor_id: "", date: today, posting_date: today, expected_date: "", due_date: "",
    external_doc_no: "", sales_order_no: "", purchaser: "", received_by: "", arrival_notify: "", location_code: "", tax_rate: 8,
    department: "", currency: "",
    ship_to_name: "", ship_to_address: "", ship_to_address2: "", ship_to_city: "", ship_to_state: "",
    ship_to_postcode: "", ship_to_country: "", incoterms: "", incoterms_location: "",
  });
  const [lines, setLines] = useState([{ item_id: "", description: "", quantity: 1, unit_price: 0 }]);

  const { data: companyProfile } = useQuery({ queryKey: ["company-profile"], queryFn: async () => (await api.get("/company-profile")).data });
  useEffect(() => {
    if (doc) {
      setForm({
        vendor_id: doc.vendor_id || "", date: doc.date || today, posting_date: doc.posting_date || doc.date || today,
        expected_date: doc.expected_date || "", due_date: doc.due_date || "", external_doc_no: doc.external_doc_no || "",
        sales_order_no: doc.sales_order_no || "", purchaser: doc.purchaser || "", received_by: doc.received_by || "", arrival_notify: doc.arrival_notify || "", location_code: doc.location_code || "", tax_rate: doc.tax_rate ?? 8,
        department: doc.department || "", currency: doc.currency || "",
        ship_to_name: doc.ship_to_name || "", ship_to_address: doc.ship_to_address || "", ship_to_address2: doc.ship_to_address2 || "",
        ship_to_city: doc.ship_to_city || "", ship_to_state: doc.ship_to_state || "", ship_to_postcode: doc.ship_to_postcode || "",
        ship_to_country: doc.ship_to_country || "", incoterms: doc.incoterms || "", incoterms_location: doc.incoterms_location || "",
      });
      setLines((doc.lines || []).length ? doc.lines.map((l) => ({ ...l })) : [{ item_id: "", description: "", quantity: 1, unit_price: 0 }]);
    }
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Blank Ship-To on new or still-open POs defaults to the company's own address; it can be overwritten
    const open = isNew || (doc && !doc.archived && ["Quote", "Order"].includes(doc.doc_type) && !["Invoiced", "Closed", "Posted"].includes(doc.status));
    if (!open || !companyProfile) return;
    setForm((f) => (["name", "address", "address2", "city", "state", "postcode", "country"].some((k) => f[`ship_to_${k}`]) ? f : {
      ...f, ship_to_name: companyProfile.name || "", ship_to_address: companyProfile.address || "", ship_to_address2: companyProfile.address2 || "",
      ship_to_city: companyProfile.city || "", ship_to_state: companyProfile.state || "", ship_to_postcode: companyProfile.postcode || "", ship_to_country: companyProfile.country || "",
    }));
  }, [isNew, companyProfile, doc]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickItem = (idx, itemId) => {
    const it = items.find((x) => x.id === itemId);
    setLines((ls) => ls.map((l, i) => i === idx ? { ...l, item_id: itemId, description: itemLineDescription(it) || l.description, unit_price: it ? vendorItemCost(it, form.vendor_id, form.currency) : l.unit_price, vendor_part_no: it?.vendor_part_no ?? l.vendor_part_no ?? "", quote_ref_no: it?.quote_ref_no ?? l.quote_ref_no ?? "", lead_time_days: it?.lead_time_days ?? l.lead_time_days ?? 0 } : l));
  };
  const setLine = (idx, k, v) => setLines((ls) => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, { item_id: "", description: "", quantity: 1, unit_price: 0 }]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const subtotal = useMemo(() => lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0), [lines]);
  const total = subtotal;
  const selVendor = vendors.find((v) => v.id === form.vendor_id);

  // Currency / AUD conversion. Rate locks at creation (doc.fx_rate_to_aud); new docs use today's live rate.
  const curCode = form.currency || "";
  const fmt = (n) => (curCode ? moneyIn(n, curCode) : money(n));
  const showAud = !!curCode && curCode !== "AUD";
  const liveRate = curCode ? Number(fxData?.rates_to_aud?.[curCode] || 0) : 1;
  const lockedRate = Number(doc?.fx_rate_to_aud || 0);
  const rateToAud = (!isNew && doc?.currency === curCode && lockedRate) ? lockedRate : liveRate;
  const totalAud = rateToAud ? total * rateToAud : 0;
  const expectedSell = useMemo(() => lines.reduce((s, l) => { const it = items.find((x) => x.id === l.item_id); return s + Number(l.quantity || 0) * Number(it?.unit_price || 0); }, 0), [lines, items]);
  const gpPct = expectedSell > 0 ? ((expectedSell - subtotal) / expectedSell) * 100 : 0;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["purchase-orders"] });
    qc.invalidateQueries({ queryKey: ["purchase-order", id] });
    qc.invalidateQueries({ queryKey: ["vendors"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["items"] });
    qc.invalidateQueries({ queryKey: ["posted-receipts"] });
  };

  const payload = () => ({
    ...(isNew ? { doc_no: form.doc_no || "" } : {}),
    vendor_id: form.vendor_id, date: form.date, posting_date: form.posting_date,
    expected_date: form.expected_date, due_date: form.due_date, external_doc_no: form.external_doc_no,
    sales_order_no: form.sales_order_no, purchaser: form.purchaser, received_by: form.received_by, arrival_notify: form.arrival_notify, location_code: form.location_code, tax_rate: 0,
    department: form.department, currency: form.currency || "",
    ship_to_name: form.ship_to_name, ship_to_address: form.ship_to_address, ship_to_address2: form.ship_to_address2,
    ship_to_city: form.ship_to_city, ship_to_state: form.ship_to_state, ship_to_postcode: form.ship_to_postcode,
    ship_to_country: form.ship_to_country, incoterms: form.incoterms, incoterms_location: form.incoterms_location,
    lines: lines.filter((l) => l.description).map((l) => ({ item_id: l.item_id || "", description: l.description, quantity: Number(l.quantity), unit_price: Number(l.unit_price), vendor_part_no: l.vendor_part_no || "", quote_ref_no: l.quote_ref_no || "", lead_time_days: Number(l.lead_time_days || 0), project_id: l.project_id || "", project_code: projects.find((p) => p.id === l.project_id)?.code || l.project_code || "" })),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return (await api.post("/purchase-orders", { doc_type: docType, status: "Open", ...payload() })).data;
      return (await api.put(`/purchase-orders/${id}`, payload())).data;
    },
    onSuccess: (res) => {
      invalidate();
      toast.success(isNew ? `${PDOC_LABEL[docType]} ${res.doc_no} created` : "Document saved");
      if (isNew) navigate(`/purchasing/doc/${res.id}?tab=${backTab}`, { replace: true });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const createdRef = useRef(false);
  const autoSaveFn = async () => {
    if (isNew) {
      if (createdRef.current) return;
      createdRef.current = true;
      try {
        const res = (await api.post("/purchase-orders", { doc_type: docType, status: "Open", ...payload() })).data;
        invalidate();
        navigate(`/purchasing/doc/${res.id}?tab=${backTab}`, { replace: true });
        return res;
      } catch (e) { createdRef.current = false; throw e; }
    }
    const res = (await api.put(`/purchase-orders/${id}`, payload())).data;
    invalidate();
    return res;
  };
  const { status: autoStatus, flush: autoSaveFlush } = useAutoSave({ active: !readOnly, canSave: !!form.vendor_id && (docType !== "Invoice" || !!form.external_doc_no?.trim()), createMode: isNew, snapshot: payload(), save: autoSaveFn });

  const [confirm, setConfirm] = useState(null);
  const convertToOrder = useMutation({ mutationFn: async () => (await api.post(`/purchase-orders/${id}/convert-to-order`)).data, onSuccess: (r) => { invalidate(); toast.success(`Converted to order ${r.doc_no}`); navigate(`/purchasing/doc/${r.id}?tab=orders`); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const [vendorInvNo, setVendorInvNo] = useState("");
  const makeInvoice = useMutation({ mutationFn: async () => (await api.post(`/purchase-orders/${id}/convert-to-invoice`, { external_doc_no: vendorInvNo.trim() })).data, onSuccess: (r) => { invalidate(); toast.success(`Purchase invoice ${r.doc_no} created${r.fully_invoiced ? "" : " for the received quantities"}`); navigate(`/purchasing/doc/${r.id}?tab=invoices`); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const del = useMutation({ mutationFn: async () => api.delete(`/purchase-orders/${id}`), onSuccess: () => { invalidate(); toast.success("Deleted"); goBack(); } });
  const correctInvoice = useMutation({ mutationFn: async () => (await api.post(`/purchase-orders/${id}/correct`)).data, onSuccess: (r) => { invalidate(); toast.success(`Corrective credit memo ${r.credit_memo.doc_no} created${r.reopened_order ? ` · ${r.reopened_order} reopened` : ""}`); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });

  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Bank Transfer");
  const openPayment = () => { setPayAmount(String(round2((doc?.total || 0) - (doc?.amount_paid || 0)))); setPayMethod("Bank Transfer"); setPayOpen(true); };
  const vendorPayment = useMutation({
    mutationFn: async () => (await api.post(`/purchase-orders/${id}/payments`, { amount: Number(payAmount), method: payMethod })).data,
    onSuccess: (r) => { invalidate(); setPayOpen(false); toast.success(r.remaining > 0 ? `Paid — ${money(r.remaining)} remaining` : "Invoice fully paid"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  // ---- email confirmation dialog ----
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [emailAttachIds, setEmailAttachIds] = useState([]);
  const [manualEmail, setManualEmail] = useState("");
  const manualList = useMemo(() => manualEmail.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean), [manualEmail]);
  const totalRecipients = selectedRecipients.length + manualList.length;
  const { data: emailRecipients } = useQuery({
    queryKey: ["po-email-recipients", id],
    queryFn: async () => (await api.get(`/purchase-orders/${id}/email-recipients`)).data,
    enabled: emailOpen && !isNew,
  });
  const recipientOptions = useMemo(() => emailRecipients?.options || [], [emailRecipients]);
  useEffect(() => {
    if (emailOpen && recipientOptions.length && selectedRecipients.length === 0) {
      const primary = recipientOptions.find((o) => o.primary) || recipientOptions[0];
      setSelectedRecipients([primary.email]);
    }
    if (!emailOpen && selectedRecipients.length > 0) setSelectedRecipients([]);
    if (!emailOpen && manualEmail) setManualEmail("");
    if (!emailOpen && emailAttachIds.length > 0) setEmailAttachIds([]);
  }, [emailOpen, recipientOptions]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleRecipient = (email) => setSelectedRecipients((rs) => rs.includes(email) ? rs.filter((e) => e !== email) : [...rs, email]);
  const [emailMessage, setEmailMessage] = useState("");
  const [messageTouched, setMessageTouched] = useState(false);
  useEffect(() => {
    if (!emailOpen) { setEmailMessage(""); setMessageTouched(false); return; }
    if (!messageTouched) setEmailMessage(emailRecipients?.message || "");
  }, [emailOpen, emailRecipients?.message]); // eslint-disable-line react-hooks/exhaustive-deps
  const emailDoc = useMutation({
    mutationFn: async () => (await api.post(`/purchase-orders/${id}/send-email`, { recipients: [...selectedRecipients, ...manualList], attachment_ids: emailAttachIds, message: emailMessage })).data,
    onSuccess: (r) => { setEmailOpen(false); invalidate(); toast.success(`Emailed to ${(r.sent_to || []).join(", ")}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  // ---- PO approval workflow ----
  const submitForApproval = useMutation({
    mutationFn: async () => (await api.post(`/purchase-orders/${id}/submit-for-approval`)).data,
    onSuccess: (r) => { invalidate(); toast.success(r.email_sent ? `Sent to ${r.approver?.name || r.approver?.email} for approval` : "Submitted (approval email failed to send)"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (!isNew && isLoading) {
    return <div className="flex items-center justify-center py-24"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const title = isNew ? `New ${PDOC_LABEL[docType]}` : `${doc.doc_no} · ${doc.vendor_name}`;
  const status = doc?.status || "Open";
  const showRecv = !isNew && docType === "Order";

  // approval workflow state
  const applicableThreshold = (() => {
    const dept = (form.department || doc?.department || "").trim().toLowerCase();
    const rule = (approvalConfig?.departments || []).find((d) => (d.department || "").trim().toLowerCase() === dept && dept);
    return rule ? Number(rule.threshold || 0) : Number(approvalConfig?.default_threshold || 0);
  })();
  const overThreshold = !!approvalConfig?.enabled && docType === "Order" && !isNew && Number(doc?.total || total || 0) > applicableThreshold;
  const approvalOn = overThreshold && doc?.created_by_role !== "admin";
  // Admin-raised POs skip approval but still show the same steps (auto-approved)
  const adminAutoApproved = overThreshold && doc?.created_by_role === "admin";
  const aStatus = doc?.approval_status || "";
  const needsApproval = approvalOn && ["", "Draft", "Declined"].includes(aStatus);
  const awaitingApproval = approvalOn && aStatus === "Pending";
  const isApproved = approvalOn && aStatus === "Approved";
  const vendorActionsAllowed = !approvalOn || ["Approved", "Sent"].includes(aStatus);
  const deptOptions = approvalConfig?.departments || [];

  return (
    <div className="animate-fade-up pb-10" data-testid="purchase-order-card" onBlur={autoSaveFlush}>
      <div className="flex items-center gap-3 mb-1">
        <button onClick={goBack} className="p-1.5 -ml-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid="pcard-back"><ArrowLeft size={18} weight="bold" /></button>
        {doc?.drop_ship && <span className="text-[10px] uppercase font-bold text-primary border border-primary/30 bg-primary/10 px-1.5 py-0.5 rounded-sm">Drop-ship → {doc.ship_to_name}</span>}
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="pcard-title">{title}</h1>
          <ViewOnlyBadge show={viewOnly && !isNew} />
          <StatusChip status={status} />
          {doc?.receipt_status === "Partially Received" && <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm border-amber-200 bg-amber-50 text-amber-700" data-testid="pcard-receipt-status">Partially Received</span>}
          {doc?.doc_type === "Order" && !doc?.archived && Number(doc?.invoiced_amount || 0) > 0 && <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm border-violet-200 bg-violet-50 text-violet-700" data-testid="pcard-invoice-status">Partially Invoiced</span>}
        </div>
        {!isNew && doc && <RecordNav source="purchase-orders" currentId={id} sortKey={docKey} basePath="/purchasing/doc" testId="purchase-nav"
          filter={(o) => (o.doc_type || "Order") === (doc.doc_type || "Order") && !!o.posted === !!doc.posted && !!o.archived === !!doc.archived} />}
      </div>

      {doc?.doc_type === "CreditMemo" && doc?.corrects_doc_no && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 border border-amber-200 bg-amber-50 text-amber-800 text-sm rounded-sm" data-testid="paudit-reverses">
          <ArrowUUpLeft size={15} weight="bold" />
          <span>Corrective credit memo — reverses{" "}
            {doc.reverses_id
              ? <button onClick={() => navigate(`/purchasing/doc/${doc.reverses_id}`)} className="font-semibold underline hover:text-amber-900" data-testid="paudit-reverses-link">{doc.corrects_doc_no}</button>
              : <span className="font-semibold">{doc.corrects_doc_no}</span>}
            {doc.corrected_by ? ` · by ${doc.corrected_by}` : ""}{doc.corrected_at ? ` on ${doc.corrected_at.slice(0, 10)}` : ""}
          </span>
        </div>
      )}
      {doc?.doc_type === "Invoice" && doc?.corrected && doc?.corrects_doc_no && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 border border-rose-200 bg-rose-50 text-rose-800 text-sm rounded-sm" data-testid="paudit-corrected">
          <ArrowUUpLeft size={15} weight="bold" />
          <span>Corrected — reversed by credit memo{" "}
            {doc.reversed_by_id
              ? <button onClick={() => navigate(`/purchasing/doc/${doc.reversed_by_id}`)} className="font-semibold underline hover:text-rose-900" data-testid="paudit-corrected-link">{doc.corrects_doc_no}</button>
              : <span className="font-semibold">{doc.corrects_doc_no}</span>}
            {doc.corrected_by ? ` · by ${doc.corrected_by}` : ""}{doc.corrected_at ? ` on ${doc.corrected_at.slice(0, 10)}` : ""}
          </span>
        </div>
      )}

      <div className="flex items-center gap-1 flex-wrap border border-border bg-card px-2 py-1.5 mb-5 rounded-sm" data-testid="pcard-ribbon">
        {!readOnly && <RibbonBtn icon={FloppyDisk} label={isNew ? "Create" : "Save"} onClick={() => save.mutate()} disabled={!form.vendor_id || save.isPending} primary testid="pribbon-save" />}
        {!readOnly && <AutoSaveIndicator status={autoStatus} isNew={isNew} testid="purchase-autosave" />}
        {!isNew && !readOnly && canPost && docType === "Quote" && <RibbonBtn icon={ArrowsClockwise} label="Make Order" onClick={() => setConfirm({ kind: "order" })} testid="pribbon-convert-order" />}
        {!isNew && !viewOnly && canPost && vendorActionsAllowed && poCanInvoice(doc) && <RibbonBtn icon={ArrowsClockwise} label="Make Invoice" onClick={() => { setVendorInvNo(form.external_doc_no || ""); setConfirm({ kind: "invoice" }); }} testid="pribbon-convert-invoice" />}
        {!isNew && !viewOnly && canPost && vendorActionsAllowed && poCanReceive(doc) && <RibbonBtn icon={SealCheck} label="Receive" onClick={() => setReceiveOpen(true)} testid="pribbon-receive" />}
        {needsApproval && !viewOnly && <RibbonBtn icon={PaperPlaneTilt} label={aStatus === "Declined" ? "Resubmit for Approval" : "Send for Approval"} onClick={() => submitForApproval.mutate()} disabled={submitForApproval.isPending} primary testid="pribbon-submit-approval" />}
        {awaitingApproval && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid="pribbon-awaiting"><Hourglass size={14} weight="bold" /> Awaiting Approval</span>}
        {!isNew && can("purchasing.payments") && docType === "Invoice" && status !== "Paid" && <RibbonBtn icon={CurrencyDollar} label="Record Payment" onClick={openPayment} testid="pribbon-payment" />}
        {!isNew && canPost && docType === "Invoice" && !doc?.corrected && <RibbonBtn icon={ArrowUUpLeft} label="Correct" onClick={() => setConfirm({ kind: "correct" })} testid="pribbon-correct" />}
        {!isNew && <RibbonBtn icon={FilePdf} label="Print PDF" onClick={() => window.open(`${API}/purchase-orders/${id}/pdf`, "_blank")} testid="pribbon-pdf" />}
        {!isNew && canPost && vendorActionsAllowed && <RibbonBtn icon={EnvelopeSimple} label={isApproved ? "Send PO to Vendor" : "Send to Vendor"} onClick={() => setEmailOpen(true)} disabled={emailDoc.isPending} primary={isApproved} testid="pribbon-email" />}
        {!isNew && !readOnly && can("purchasing.delete") && <RibbonBtn icon={Trash} label="Delete" onClick={() => setConfirm({ kind: "delete" })} testid="pribbon-delete" />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Section title="General">
            {isNew && !readOnly && (
              <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5" data-testid="required-legend">
                <span className="text-red-500 font-bold">*</span> Required fields are marked and highlighted until completed.
              </p>
            )}
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-3 md:gap-x-6 gap-y-3">
              {isNew && <DocNoField counter={`purchase_${docType}`} value={form.doc_no || ""} onChange={(v) => setF("doc_no", v)} testid="pcard-doc-no" />}
              <Field label="Vendor No." required={!readOnly} highlight={isNew && !readOnly && !form.vendor_id}>
                {readOnly ? <ReadOnly value={selVendor?.code || doc.vendor_name} /> : (
                  <Select value={form.vendor_id} onValueChange={(v) => { const ven = vendors.find((x) => x.id === v); setForm((f) => ({ ...f, vendor_id: v, ...(ven && v !== f.vendor_id ? { currency: ven.currency || "" } : {}) })); }}>
                    <SelectTrigger className="rounded-sm font-mono" data-testid="pcard-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.code ? `${v.code} · ` : ""}{v.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Vendor Name"><ReadOnly value={selVendor?.name || doc?.vendor_name} /></Field>
              <Field label="External Document No." required={!readOnly && docType === "Invoice"} highlight={!readOnly && docType === "Invoice" && !form.external_doc_no?.trim()}>
                {readOnly ? <ReadOnly value={form.external_doc_no} /> : <Input data-testid="pcard-ext-doc" value={form.external_doc_no} onChange={(e) => setF("external_doc_no", e.target.value)} className="rounded-sm" placeholder="Vendor invoice / reference" />}
              </Field>

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Buy-from</p>
              <Field label="Address"><ReadOnly value={selVendor?.address} /></Field>
              <Field label="Address 2"><ReadOnly value={selVendor?.address2} /></Field>
              <Field label="City"><ReadOnly value={selVendor?.city} /></Field>
              <Field label="Postcode"><ReadOnly value={selVendor?.postcode} /></Field>
              <Field label="State / Province"><ReadOnly value={selVendor?.state} /></Field>
              <Field label="Country/Region Code"><ReadOnly value={selVendor?.country} /></Field>
              <Field label="Contact No."><ReadOnly value={selVendor?.code} /></Field>
              <Field label="Phone No."><ReadOnly value={selVendor?.phone} /></Field>
              <Field label="Email"><ReadOnly value={selVendor?.email} /></Field>

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Dates</p>
              <Field label="Document Date" required={!readOnly} highlight={isNew && !readOnly && !form.date}>
                {readOnly ? <ReadOnly value={form.date} /> : <Input type="date" data-testid="pcard-date" value={form.date} onChange={(e) => setF("date", e.target.value)} className="rounded-sm" />}
              </Field>
              <Field label="Posting Date">
                {readOnly ? <ReadOnly value={form.posting_date} /> : <Input type="date" value={form.posting_date} onChange={(e) => setF("posting_date", e.target.value)} className="rounded-sm" data-testid="pcard-posting-date" />}
                {!isNew && !viewOnly && docType === "Order" && <PostingDateHint doc={{ ...doc, posting_date: form.posting_date }} kind="purchase-orders" isAdmin={isAdmin} onDone={() => { setF("posting_date", localToday()); invalidate(); }} />}
              </Field>
              <Field label="Expected Receipt Date">
                {readOnly ? <ReadOnly value={form.expected_date} /> : <Input type="date" value={form.expected_date} onChange={(e) => setF("expected_date", e.target.value)} className="rounded-sm" />}
              </Field>
              <Field label="Due Date">
                {readOnly ? <ReadOnly value={form.due_date} /> : <Input type="date" value={form.due_date} onChange={(e) => setF("due_date", e.target.value)} className="rounded-sm" />}
              </Field>
              <Field label="Received By">
                {readOnly ? <ReadOnly value={form.received_by} /> : <Input value={form.received_by} onChange={(e) => setF("received_by", e.target.value)} placeholder="Who will receive the goods" className="rounded-sm" data-testid="pcard-received-by" />}
              </Field>
              <Field label="Notify on Arrival">
                {readOnly ? <ReadOnly value={form.arrival_notify} /> : <Input value={form.arrival_notify} onChange={(e) => setF("arrival_notify", e.target.value)} placeholder="Other emails, e.g. warehouse@…" className="rounded-sm" data-testid="pcard-arrival-notify" />}
              </Field>

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Invoicing</p>
              <Field label="Purchaser Code">
                {readOnly ? <ReadOnly value={form.purchaser} /> : (
                  <Select value={form.purchaser || "none"} onValueChange={(v) => setF("purchaser", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="pcard-purchaser"><SelectValue placeholder="Select purchaser" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {salespeople.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Location Code">
                {readOnly ? <ReadOnly value={form.location_code} /> : (
                  <Select value={form.location_code || "none"} onValueChange={(v) => setF("location_code", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="pcard-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {locations.map((l) => <SelectItem key={l.id} value={l.code}>{l.code} · {l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Sales Order No.">
                {readOnly ? <ReadOnly value={form.sales_order_no} /> : (
                  <Select value={form.sales_order_no || "none"} onValueChange={(v) => setF("sales_order_no", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="pcard-sales-order"><SelectValue placeholder="Link a sales order" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {salesOrderOptions.map((so) => <SelectItem key={so.id} value={so.doc_no}>{so.doc_no} · {so.customer_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Status"><ReadOnly value={status} /></Field>
              <Field label="Currency">
                {readOnly ? <ReadOnly value={form.currency || "—"} /> : (
                  <Select value={form.currency || "none"} onValueChange={(v) => setF("currency", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="pcard-currency"><SelectValue placeholder="Select currency" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {approvalOn && deptOptions.length > 0 && (
                <Field label="Department">
                  {readOnly ? <ReadOnly value={form.department} /> : (
                    <Select value={form.department || "none"} onValueChange={(v) => setF("department", v === "none" ? "" : v)}>
                      <SelectTrigger className="rounded-sm" data-testid="pcard-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Default approver —</SelectItem>
                        {deptOptions.map((d) => <SelectItem key={d.department || d} value={d.department || d}>{d.department || d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              )}

              <p className="overline-label col-span-2 xl:col-span-3 pt-2 border-t border-border/60 text-primary">Totals</p>
              <Field label="Subtotal"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-medium" data-testid="pgen-subtotal">{fmt(subtotal)}</div></Field>
              <Field label={`Total${curCode ? ` (${curCode})` : ""}`}><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-bold text-primary" data-testid="pgen-total">{fmt(total)}</div></Field>
              {showAud && (
                <Field label="Total in AUD"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-semibold" data-testid="pgen-total-aud">{rateToAud ? moneyIn(totalAud, "AUD") : "Rate unavailable"}</div></Field>
              )}
              {showAud && (
                <Field label="FX Rate → AUD"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular" data-testid="pgen-fx-rate">{rateToAud ? `1 ${curCode} = ${rateToAud.toFixed(4)} AUD` : "—"}{(!isNew && doc?.fx_rate_date) ? ` · ${doc.fx_rate_date}` : " · live"}</div></Field>
              )}
              <Field label="Expected Sell Price (at list)"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular" data-testid="pgen-sell">{fmt(expectedSell)}</div></Field>
              <Field label="GP %"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm tabular font-semibold" data-testid="pgen-gp">{gpPct.toFixed(1)}</div></Field>
            </div>
          </Section>

          <Section title="Shipping and Incoterms" mobileCollapsed>
            <div className="space-y-4">
              <Field label="Ship-to Name">
                {readOnly ? <ReadOnly value={form.ship_to_name} /> : <Input data-testid="pcard-ship-name" value={form.ship_to_name} onChange={(e) => setF("ship_to_name", e.target.value)} className="rounded-sm" placeholder="Deliver to" />}
              </Field>
              {readOnly ? (
                <Field label="Ship-to Address">
                  <ReadOnly value={[form.ship_to_address, form.ship_to_address2, [form.ship_to_city, form.ship_to_state, form.ship_to_postcode].filter(Boolean).join(" "), form.ship_to_country].filter(Boolean).join(", ")} />
                </Field>
              ) : (
                <AddressFields values={form} onChange={setF} prefix="ship_to_" testidPrefix="pcard-ship" />
              )}
              <div className="pt-1 border-t border-border/60">
                {readOnly ? (
                  <Field label="Incoterms"><ReadOnly value={[form.incoterms, form.incoterms_location].filter(Boolean).join(" — ")} /></Field>
                ) : (
                  <IncotermsFields incoterms={form.incoterms} location={form.incoterms_location} onChange={setF} testidPrefix="pcard" />
                )}
              </div>
            </div>
          </Section>

          <Section title="Lines" subtitle={isNew && !readOnly ? "at least one line with a description and quantity is required" : undefined}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pcard-lines-table">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="py-2 pr-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-14">Type</th>
                    <th className="py-2 pr-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold min-w-[220px]">Item / Description{isNew && !readOnly && <span className="text-red-500 ml-0.5">*</span>}</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-40">Project</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-24 text-right">Quantity{isNew && !readOnly && <span className="text-red-500 ml-0.5">*</span>}</th>
                    {showRecv && <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-20 text-right">Received</th>}
                    {showRecv && <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-20 text-right">Invoiced</th>}
                    <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-28 text-right">Unit Cost</th>
                    <th className="py-2 px-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold w-28 text-right">Amount</th>
                    {!readOnly && <th className="w-8" />}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => (
                    <tr key={idx} className="border-b border-border/60" data-testid="pcard-line-row">
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">Item</td>
                      <td className="py-1.5 pr-2">
                        {readOnly ? (
                          <div className="flex flex-col">
                            <LinePartNo itemId={l.item_id} items={items} description={l.description} testid="pcard-line-part-no" />
                            <span data-testid="pcard-line-desc-text">{l.description}</span>
                            {(l.vendor_part_no || l.quote_ref_no || l.lead_time_days) ? (
                              <span className="text-[10px] text-muted-foreground" data-testid="pcard-line-replen">
                                {[l.vendor_part_no && `P/N: ${l.vendor_part_no}`, l.quote_ref_no && `Quote: ${l.quote_ref_no}`, l.lead_time_days ? `Lead: ${Number(l.lead_time_days)}d` : ""].filter(Boolean).join(" · ")}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <ItemPicker value={l.item_id || ""} items={items} onChange={(v) => pickItem(idx, v)} placeholder="Select item (optional)" testid="pcard-line-item" />
                            <Input value={l.description} onChange={(e) => setLine(idx, "description", e.target.value)} onBlur={() => { const it = items.find((x) => x.id === l.item_id); if (it && !(l.description || "").trim()) setLine(idx, "description", itemLineDescription(it)); }} className={`rounded-sm h-8 text-sm ${isNew && !l.description ? "ring-1 ring-amber-400 border-amber-400" : ""}`} placeholder="Description" data-testid="pcard-line-desc" />
                            <UseItemDescription item={items.find((x) => x.id === l.item_id)} description={l.description} onUse={(d) => setLine(idx, "description", d)} testid="pcard-line-use-item-desc" />
                            <div className="flex gap-1">
                              <Input value={l.vendor_part_no || ""} onChange={(e) => setLine(idx, "vendor_part_no", e.target.value)} className="rounded-sm h-7 text-xs" placeholder="Vendor Part No." data-testid="pcard-line-vpn" />
                              <Input value={l.quote_ref_no || ""} onChange={(e) => setLine(idx, "quote_ref_no", e.target.value)} className="rounded-sm h-7 text-xs" placeholder="Quote Ref No." data-testid="pcard-line-quoteref" />
                              <Input type="number" value={l.lead_time_days || 0} onChange={(e) => setLine(idx, "lead_time_days", e.target.value)} className="rounded-sm h-7 text-xs num w-20" placeholder="Lead (d)" data-testid="pcard-line-leadtime" />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 px-2 align-top">
                        {readOnly ? <span className="font-mono text-xs" data-testid="pcard-line-project-ro">{l.project_code || "—"}</span> : (
                          <Select value={l.project_id || "none"} onValueChange={(v) => setLine(idx, "project_id", v === "none" ? "" : v)}>
                            <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="pcard-line-project"><SelectValue placeholder="No project" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No project</SelectItem>
                              {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`pcard-line-project-opt-${p.code}`}>{p.code} · {p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        {readOnly ? <span className="tabular">{l.quantity}</span> : <Input type="number" value={l.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} className={`rounded-sm h-8 num text-right ${isNew && !(Number(l.quantity) > 0) ? "ring-1 ring-amber-400 border-amber-400" : ""}`} data-testid="pcard-line-qty" />}
                      </td>
                      {showRecv && <td className="py-1.5 px-2 text-right tabular text-sm" data-testid="pcard-line-received">{qtyReceived(l, doc)}</td>}
                      {showRecv && <td className="py-1.5 px-2 text-right tabular text-sm text-muted-foreground" data-testid="pcard-line-invoiced">{Number(l.qty_invoiced || 0)}{qtyToInvoice(l, doc) > 0 && !doc.archived ? <span className="block text-[10px] text-violet-600">{qtyToInvoice(l, doc)} to invoice</span> : null}</td>}
                      <td className="py-1.5 px-2 text-right">
                        {readOnly ? <span className="tabular">{money(l.unit_price)}</span> : <Input type="number" value={l.unit_price} onChange={(e) => setLine(idx, "unit_price", e.target.value)} className="rounded-sm h-8 num text-right" data-testid="pcard-line-price" />}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular font-medium">{money(Number(l.quantity || 0) * Number(l.unit_price || 0))}</td>
                      {!readOnly && (
                        <td className="py-1.5 text-right">
                          {lines.length > 1 && <button onClick={() => removeLine(idx)} className="p-1 text-muted-foreground hover:text-destructive" data-testid="pcard-line-delete"><Trash size={15} /></button>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!readOnly && <button onClick={addLine} data-testid="pcard-add-line" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><Plus size={14} weight="bold" /> Add line</button>}
          </Section>

          <Section title="Totals">
            <div className="max-w-sm ml-auto space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular font-medium">{fmt(subtotal)}</span></div>
              <div className="flex items-center justify-between border-t border-border pt-2 text-base font-bold"><span>Total{curCode ? ` (${curCode})` : ""}</span><span className="tabular text-primary" data-testid="pcard-total">{fmt(total)}</span></div>
              {showAud && (
                <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="pcard-total-aud">
                  <span>≈ in AUD{rateToAud ? ` @ ${rateToAud.toFixed(4)}` : ""}</span>
                  <span className="tabular font-semibold text-foreground">{rateToAud ? moneyIn(totalAud, "AUD") : "Rate unavailable"}</span>
                </div>
              )}
            </div>
          </Section>
        </div>

        <div className="lg:col-span-1">
          <div className="border border-border bg-card sticky top-4" data-testid="pcard-info-pane">
            <Tabs defaultValue="details">
              <TabsList className="w-full rounded-none border-b border-border bg-secondary/40 h-auto p-0">
                <TabsTrigger value="details" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="ppane-tab-details"><ListChecks size={14} className="mr-1" /> Details</TabsTrigger>
                <TabsTrigger value="notes" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="ppane-tab-notes"><NoteIcon size={14} className="mr-1" /> Notes</TabsTrigger>
                <TabsTrigger value="files" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="ppane-tab-files"><Paperclip size={14} className="mr-1" /> Files</TabsTrigger>
                <TabsTrigger value="summary" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="ppane-tab-summary"><Sparkle size={14} className="mr-1" /> Summary</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="p-4 space-y-2.5 text-sm mt-0">
                <PaneRow label="Document" value={isNew ? "— (unsaved)" : doc.doc_no} mono />
                <PaneRow label="Vendor" value={form.vendor_id ? (vendors.find((v) => v.id === form.vendor_id)?.name || doc?.vendor_name) : "—"} />
                <PaneRow label="Status" value={status} />
                <PaneRow label="External Ref" value={form.external_doc_no || "—"} mono />
                <PaneRow label="Sales Order" value={form.sales_order_no || "—"} mono />
                <div className="border-t border-border pt-2.5 space-y-2.5">
                  <PaneRow label="Currency" value={curCode || "—"} />
                  <PaneRow label="Subtotal" value={fmt(subtotal)} tabular />
                  <PaneRow label="Total" value={fmt(total)} tabular bold />
                  {showAud && <PaneRow label="Total (AUD)" value={rateToAud ? moneyIn(totalAud, "AUD") : "—"} tabular />}
                  {showAud && <PaneRow label="FX Rate" value={rateToAud ? `1 ${curCode} = ${rateToAud.toFixed(4)} AUD` : "—"} tabular />}
                  {showAud && <PaneRow label="Rate Date" value={(!isNew && doc?.fx_rate_date) ? doc.fx_rate_date : "Live (unsaved)"} />}
                  {!isNew && docType === "Invoice" && (
                    <>
                      <PaneRow label="Amount Paid" value={fmt(doc.amount_paid || 0)} tabular />
                      <PaneRow label="Remaining" value={fmt((doc.total || 0) - (doc.amount_paid || 0))} tabular />
                    </>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="notes" className="p-4 mt-0">
                <NotesPane docId={id} isNew={isNew} readOnly={viewOnly} notes={doc?.notes || []} onAdded={() => qc.invalidateQueries({ queryKey: ["purchase-order", id] })} />
              </TabsContent>
              <TabsContent value="files" className="p-4 mt-0">
                <AttachmentsPane entityType="purchase-order" entityId={id} isNew={isNew} readOnly={viewOnly} label="Upload Vendor Invoice" />
              </TabsContent>
              <TabsContent value="summary" className="p-4 mt-0">
                <SummaryPane docId={id} isNew={isNew} existing={doc?.ai_summary} onDone={() => qc.invalidateQueries({ queryKey: ["purchase-order", id] })} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {approvalOn && <ApprovalStepper doc={doc} aStatus={aStatus} />}
      {adminAutoApproved && <ApprovalStepper doc={doc} aStatus={doc.approval_sent_at ? "Sent" : "Approved"} autoApproved />}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CurrencyDollar size={20} className="text-primary" /> Pay Vendor Invoice</DialogTitle></DialogHeader>
          {doc && (
            <div className="space-y-4 py-2">
              <div className="border border-border bg-secondary/50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-mono font-medium">{doc.doc_no}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="tabular">{money(doc.total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Already paid</span><span className="tabular">{money(doc.amount_paid || 0)}</span></div>
                <div className="flex justify-between font-bold"><span>Remaining</span><span className="tabular text-primary">{money(doc.total - (doc.amount_paid || 0))}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="overline-label">Amount</Label><Input type="number" step="0.01" data-testid="pcard-payment-amount" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-1 rounded-sm num" /></div>
                <div>
                  <Label className="overline-label">Method</Label>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="pcard-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => vendorPayment.mutate()} disabled={!payAmount || Number(payAmount) <= 0 || vendorPayment.isPending} data-testid="pcard-save-payment" className="rounded-sm bg-primary hover:bg-primary/90">{vendorPayment.isPending ? "Saving…" : "Record Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pemail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EnvelopeSimple size={20} className="text-primary" /> Email {PDOC_LABEL[docType]} to Vendor
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div>
                <Label className="overline-label">Send to</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Pick addresses on file for {doc?.vendor_name || "this vendor"}, or type any email below.</p>
              </div>
              <div className="border border-border rounded-sm divide-y divide-border max-h-44 overflow-y-auto" data-testid="pemail-recipient-list">
                {recipientOptions.length === 0 && (
                  <p className="text-sm text-muted-foreground p-3" data-testid="pemail-no-recipients">No email addresses on file. Type an address below.</p>
                )}
                {recipientOptions.map((o) => (
                  <label key={o.email} className="flex items-start gap-2.5 p-2.5 cursor-pointer hover:bg-secondary/40" data-testid="pemail-recipient-option">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.includes(o.email)}
                      onChange={() => toggleRecipient(o.email)}
                      className="mt-0.5 accent-primary h-4 w-4"
                      data-testid={`pemail-recipient-checkbox-${o.email}`}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{o.label}{o.primary && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-primary font-semibold">Primary</span>}</span>
                      <span className="block text-xs text-muted-foreground font-mono truncate">{o.email}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div>
                <Label className="overline-label" htmlFor="pemail-manual-input">Add any email address</Label>
                <Input
                  id="pemail-manual-input"
                  type="text"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  placeholder="name@example.com, another@example.com"
                  className="rounded-sm mt-1"
                  data-testid="pemail-manual-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Separate multiple addresses with commas. These don't need to be on file.</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="overline-label">Attachment</Label>
                {!isNew && (
                  <button
                    onClick={() => window.open(`${API}/purchase-orders/${id}/pdf`, "_blank")}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    data-testid="pemail-download-pdf"
                  >
                    <FilePdf size={14} weight="bold" /> Download PDF
                  </button>
                )}
              </div>
              <div className="border border-border rounded-sm overflow-hidden bg-secondary/30" style={{ height: 300 }}>
                {!isNew && (
                  <iframe
                    title="Purchase order PDF preview"
                    src={`${API}/purchase-orders/${id}/pdf`}
                    className="w-full h-full"
                    data-testid="pemail-pdf-preview"
                  />
                )}
              </div>
              <p className="text-[11px] text-muted-foreground font-mono truncate">{doc?.doc_no}.pdf — attached to this email</p>
            </div>

            {!isNew && <EmailAttachmentPicker entityType="purchase-order" entityId={id} value={emailAttachIds} onChange={setEmailAttachIds} />}
            {!isNew && <POEmailMessage orderId={id} message={emailMessage} onChange={(v) => { setEmailMessage(v); setMessageTouched(true); }} templateMessage={emailRecipients?.message || ""} source={emailRecipients?.template_source} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-sm" data-testid="pemail-cancel">Cancel</Button>
            <Button
              onClick={() => emailDoc.mutate()}
              disabled={totalRecipients === 0 || emailDoc.isPending}
              className="rounded-sm bg-primary hover:bg-primary/90"
              data-testid="pemail-send"
            >
              {emailDoc.isPending ? "Sending…" : `Send${totalRecipients ? ` (${totalRecipients})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReceiveDialog open={receiveOpen} onOpenChange={setReceiveOpen} po={doc} onDone={invalidate} />

      <ConfirmDialog
        open={!!confirm}
        confirmDisabled={confirm?.kind === "invoice" && !vendorInvNo.trim()}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title={confirm?.kind === "delete" ? "Delete this document?" : confirm?.kind === "order" ? "Convert quote to order?" : confirm?.kind === "correct" ? "Correct this purchase invoice?" : "Create purchase invoice?"}
        description={confirm?.kind === "delete" ? `${doc?.doc_no} will be permanently deleted.` : confirm?.kind === "correct" ? `A corrective credit memo will be posted to reverse ${doc?.doc_no}, and its source order will reopen. To also remove received goods from stock, undo the related receipt afterwards.` : confirm?.kind === "invoice" ? `A purchase invoice will be created for everything received on ${doc?.doc_no} that hasn't been invoiced yet. Quantities still to arrive stay open on the order.` : `${doc?.doc_no} will be converted. This cannot be undone.`}
        confirmLabel={confirm?.kind === "delete" ? "Delete" : confirm?.kind === "order" ? "Convert to Order" : confirm?.kind === "correct" ? "Create Credit Memo" : "Create Invoice"}
        testid="pcard-confirm-dialog"
        onConfirm={() => {
          if (confirm?.kind === "delete") del.mutate();
          else if (confirm?.kind === "order") convertToOrder.mutate();
          else if (confirm?.kind === "correct") correctInvoice.mutate();
          else if (confirm?.kind === "invoice") makeInvoice.mutate();
          setConfirm(null);
        }}
      >
        {confirm?.kind === "invoice" && (
          <Field label="Vendor Invoice No. (External Document No.)" required highlight={!vendorInvNo.trim()}>
            <Input value={vendorInvNo} onChange={(e) => setVendorInvNo(e.target.value)} className="rounded-sm" placeholder="e.g. INV-12345" data-testid="pinvoice-ext-doc" autoFocus />
          </Field>
        )}
      </ConfirmDialog>
    </div>
  );
}

function ApprovalStepper({ doc, aStatus, autoApproved = false }) {
  const d = (s) => (s ? String(s).slice(0, 10) : "");
  const declined = aStatus === "Declined";
  const order = ["Draft", "Pending", "Approved", "Sent"];
  const effective = aStatus === "" ? "Draft" : aStatus;
  const currentIdx = declined ? 1 : Math.max(0, order.indexOf(effective));
  const steps = autoApproved ? [
    { label: "Draft", icon: Circle, sub: doc.created_by_name ? `by ${doc.created_by_name}` : "Order created" },
    { label: "Pending Approval", icon: Hourglass, sub: "Skipped (administrator)" },
    { label: "Approved", icon: CheckCircle, sub: "Auto-approved" },
    { label: "Sent to Vendor", icon: PaperPlaneTilt, sub: doc.approval_sent_at ? d(doc.approval_sent_at) : "" },
  ] : [
    { label: "Draft", icon: Circle, sub: doc.approval_submitted_by ? `by ${doc.approval_submitted_by}` : "Order created" },
    { label: "Pending Approval", icon: Hourglass, sub: doc.approval_submitted_at ? d(doc.approval_submitted_at) : (doc.approval_approver_name ? `→ ${doc.approval_approver_name}` : "Awaiting approver") },
    { label: declined ? "Declined" : "Approved", icon: declined ? XCircle : CheckCircle, sub: doc.approval_decided_at ? d(doc.approval_decided_at) : "" },
    { label: "Sent to Vendor", icon: PaperPlaneTilt, sub: doc.approval_sent_at ? d(doc.approval_sent_at) : "" },
  ];
  return (
    <div className="mt-6 border border-border bg-card rounded-sm p-5" data-testid="po-approval-footer">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <p className="overline-label text-primary">Approval Workflow</p>
        {autoApproved ? <p className="text-xs text-muted-foreground" data-testid="po-approval-auto">Raised by an administrator — approved automatically</p>
          : doc.approval_approver_name && <p className="text-xs text-muted-foreground" data-testid="po-approval-approver">Approver: <span className="font-medium text-foreground">{doc.approval_approver_name}</span>{doc.approval_approver_email ? ` · ${doc.approval_approver_email}` : ""}</p>}
      </div>
      <div className="flex items-start" data-testid={`po-approval-status-${aStatus || "Draft"}`}>
        {steps.map((s, i) => {
          const isDeclinedNode = declined && i === 2;
          const active = i === currentIdx;
          const complete = i < currentIdx || (aStatus === "Sent" && i <= 3);
          const on = active || complete;
          const Icon = s.icon;
          const color = isDeclinedNode ? "bg-rose-600 text-white border-rose-600" : on ? "bg-primary text-white border-primary" : "bg-secondary text-muted-foreground border-border";
          const lineColor = i < currentIdx && !declined ? "bg-primary" : "bg-border";
          return (
            <div key={i} className="flex-1 flex flex-col items-center relative">
              {i > 0 && <div className={`absolute top-4 right-1/2 left-[-50%] h-0.5 ${lineColor}`} />}
              <div className={`relative z-10 h-8 w-8 rounded-full border-2 flex items-center justify-center ${color}`}>
                <Icon size={16} weight="bold" />
              </div>
              <p className={`text-xs font-semibold mt-2 text-center ${isDeclinedNode ? "text-rose-600" : on ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</p>
              {s.sub && <p className="text-[10px] text-muted-foreground text-center mt-0.5 max-w-[120px] truncate">{s.sub}</p>}
            </div>
          );
        })}
      </div>

      {declined && doc.approval_decline_reason && (
        <div className="mt-4 px-3 py-2 border border-rose-200 bg-rose-50 text-rose-800 text-sm rounded-sm" data-testid="po-decline-reason-footer">
          <span className="font-semibold">Decline reason:</span> {doc.approval_decline_reason}
        </div>
      )}

      {aStatus === "Sent" && (
        <div className="mt-4 px-4 py-3 border border-emerald-200 bg-emerald-50 rounded-sm" data-testid="po-sent-summary">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-1.5"><CheckCircle size={16} weight="fill" /> PO sent to vendor</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <div><span className="text-emerald-700/70 text-xs">PO Number</span><div className="font-mono font-medium">{doc.doc_no}</div></div>
            <div><span className="text-emerald-700/70 text-xs">Vendor</span><div className="font-medium">{doc.vendor_name}</div></div>
            <div><span className="text-emerald-700/70 text-xs">Sales Order</span><div className="font-mono font-medium">{doc.sales_order_no || "—"}</div></div>
          </div>
        </div>
      )}

      {(doc.approval_history || []).length > 0 && (
        <div className="mt-5 pt-4 border-t border-border" data-testid="po-approval-history">
          <p className="overline-label text-muted-foreground mb-2">History</p>
          <ul className="space-y-1.5">
            {doc.approval_history.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-xs" data-testid="po-approval-history-row">
                <span className={`mt-0.5 h-1.5 w-1.5 rounded-full shrink-0 ${h.action === "Declined" ? "bg-rose-500" : h.action === "Approved" || h.action === "Sent to Vendor" ? "bg-emerald-500" : "bg-primary"}`} />
                <span className="text-muted-foreground">
                  <span className="font-semibold text-foreground">{h.action}</span>
                  {h.by ? ` by ${h.by}` : ""}{h.at ? ` · ${String(h.at).slice(0, 16).replace("T", " ")}` : ""}
                  {h.note ? ` — ${h.note}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function NotesPane({ docId, isNew, notes, onAdded, readOnly }) {
  const [text, setText] = useState("");
  const add = useMutation({
    mutationFn: async () => (await api.post(`/purchase-orders/${docId}/notes`, { text })).data,
    onSuccess: () => { setText(""); onAdded(); toast.success("Note added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  if (isNew) return <p className="text-sm text-muted-foreground">Save the document to add notes.</p>;
  return (
    <div className="space-y-3">
      <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="pnotes-list">
        {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
        {notes.map((n) => (
          <div key={n.id} className="border border-border rounded-sm p-2.5 text-sm" data-testid="pnote-item">
            <p className="whitespace-pre-wrap">{n.text}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{n.created_by} · {(n.created_at || "").slice(0, 10)}</p>
          </div>
        ))}
      </div>
      {!readOnly && <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add a note…" data-testid="pnote-input" className="w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" />}
      {!readOnly && <Button onClick={() => add.mutate()} disabled={!text.trim() || add.isPending} data-testid="padd-note-button" className="rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full"><Plus size={14} className="mr-1" /> Add Note</Button>}
    </div>
  );
}

function SummaryPane({ docId, isNew, existing, onDone }) {
  const [summary, setSummary] = useState(existing || "");
  useEffect(() => { setSummary(existing || ""); }, [existing]);
  const gen = useMutation({
    mutationFn: async () => (await api.post(`/ai/summarize-purchase-order/${docId}`)).data,
    onSuccess: (r) => { setSummary(r.summary); onDone(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate summary"),
  });
  if (isNew) return <p className="text-sm text-muted-foreground">Save the document to generate an Atlas summary.</p>;
  return (
    <div className="space-y-3">
      {summary ? <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="pai-summary-text">{summary}</p> : <p className="text-sm text-muted-foreground">Let Atlas summarize this document.</p>}
      <Button onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="pgenerate-summary-button" className="rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full">
        {gen.isPending ? <><div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" /> Generating…</> : <><Sparkle size={14} weight="fill" className="mr-1" /> {summary ? "Regenerate" : "Generate"} Summary</>}
      </Button>
    </div>
  );
}
